import { NODE_THRESHOLDS, FTSO_SUBMISSIONS_THRESHOLDS, FTSO_SUMMARY_HIT_RATE_THRESHOLDS, BALANCE_THRESHOLDS, FTSO_EVALUATION_TIMEFRAME, FDC_SUBMISSIONS_THRESHOLDS, EXTERNAL_VALIDATION_THRESHOLDS, DATABASE_SYNC_THRESHOLDS } from '../config/thresholds.js'; // Import thresholds and FTSO_EVALUATION_TIMEFRAME

export const StatusEvaluator = {
    indexers: (data) => {
      if (!data) return 'red';
      let hasGreen = false;
      let hasAmber = false;
      let hasRed = false;
  
      Object.entries(data).forEach(([entity, networks]) => {
        Object.entries(networks).forEach(([network, environments]) => {
          Object.entries(environments).forEach(([env, envData]) => {
            const diff = envData.block_difference;
            if (diff === null || diff === undefined) {
              hasRed = true;
            } else if (diff <= 4) {
              hasGreen = true;
            } else if (diff <= 10) {
              hasAmber = true;
            } else {
              hasRed = true;
            }
          });
        });
      });
  
      if (hasRed) return 'red';
      if (hasAmber) return 'amber';
      if (hasGreen) return 'green';
      return 'red';
    },
  
    wallets: (rows) => {
      if (!rows || rows.length === 0) return 'red';
      
      let overallStatus = 'green';
      
      rows.forEach(row => {
        if (row.flareBalance !== null) {
          const flareBalance = Number(row.flareBalance);
          if (flareBalance < BALANCE_THRESHOLDS.RED_BELOW) {
            overallStatus = 'red';
          } else if (flareBalance < BALANCE_THRESHOLDS.AMBER_BELOW && overallStatus !== 'red') {
            overallStatus = 'amber';
          }
        }
        
        if (overallStatus === 'red') return;

        if (row.songbirdBalance !== null) {
          const songbirdBalance = Number(row.songbirdBalance);
          if (songbirdBalance < BALANCE_THRESHOLDS.RED_BELOW) {
            overallStatus = 'red';
          } else if (songbirdBalance < BALANCE_THRESHOLDS.AMBER_BELOW && overallStatus !== 'red') {
            overallStatus = 'amber';
          }
        }
        if (overallStatus === 'red') return;
      });
  
      return overallStatus;
    },

    ftsoSubmissions: (submissionSummaryData, resultsMetrics, submit2PricesData, feedConfigData) => {
      if (!submissionSummaryData || typeof submissionSummaryData !== 'object' || Object.keys(submissionSummaryData).length === 0 ||
          !resultsMetrics || typeof resultsMetrics !== 'object' ||
          !submit2PricesData || typeof submit2PricesData !== 'object' ||
          !feedConfigData || typeof feedConfigData !== 'object' ) {
        console.warn("FTSO Submissions Evaluator: Missing critical data (submissionSummary, resultsMetrics, submit2Prices, or feedConfig).");
        return 'red'; 
      }
  
      let overallStatus = 'green'; 
      const entities = ['UYS', 'ENV'];
      const networks = ['flare', 'songbird'];
      const submissionTypes = ['submit1', 'submit2', 'submitSignatures'];
      const timeframeForEval = FTSO_EVALUATION_TIMEFRAME; // Use imported constant
  
      for (const entity of entities) {
        for (const network of networks) {
          if (overallStatus === 'red') break;

          const entitySubmissionSummary = submissionSummaryData[entity]?.[network];
          const entityFeedConfig = feedConfigData[entity]?.[network];
          const entitySubmit2Prices = submit2PricesData[entity]?.[network];

          if (!entitySubmissionSummary || !entityFeedConfig || !entitySubmit2Prices) {
            if(overallStatus === 'green') overallStatus = 'amber';
            console.warn(`FTSO Submissions Evaluator: Missing data for FTSO Summary Hit Rate calc for ${entity}/${network}`);
            continue;
          }

          let expectedFeeds = (entityFeedConfig || [])
            .filter(feed => feed?.feed_name?.trim().toLowerCase() !== 'null' && feed.submit === true)
            .map(feed => feed.feed_name.trim().toLowerCase());
          
          const feedCount = expectedFeeds.length;
          if (feedCount === 0) {
            continue;
          }

          const latestVotingRound = entitySubmissionSummary.latest_voting_round;
          const lastVotingRoundForTimeframe = entitySubmissionSummary[`${timeframeForEval}_last_voting_round`];

          let totalResultSumTimeframe = 0;
          let totalExpectedSubmissionsTimeframe = 0;

          for (let round = lastVotingRoundForTimeframe + 1; round <= latestVotingRound; round++) {
            const roundStr = round.toString();
            const roundResults = entitySubmit2Prices[roundStr] || [];
            const feedResultsMap = {};
            roundResults.forEach(feedData => {
              const feedKey = feedData.feed?.trim().toLowerCase();
              if(feedKey) feedResultsMap[feedKey] = Number(feedData.result);
            });

            expectedFeeds.forEach(feedName => {
              totalExpectedSubmissionsTimeframe += 1;
              const result = feedResultsMap[feedName];
              if (typeof result === 'number' && !isNaN(result)) {
                totalResultSumTimeframe += result;
              } else {
                totalResultSumTimeframe += 0;
              }
            });
          }
          
          let hitRateTimeframe = 0;
          if (totalExpectedSubmissionsTimeframe > 0) {
            hitRateTimeframe = (totalResultSumTimeframe / totalExpectedSubmissionsTimeframe) * 100;
          } else if (feedCount > 0) {
            hitRateTimeframe = 0;
          }

          if (hitRateTimeframe < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.RED_BELOW) {
            overallStatus = 'red';
          } else if (hitRateTimeframe < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.AMBER_BELOW && overallStatus !== 'red') {
            overallStatus = 'amber';
          }
        }
        if (overallStatus === 'red') break;
      }

      if (overallStatus === 'red') return 'red';

      for (const entity of entities) {
        for (const network of networks) {
          const networkSubmissionSummary = submissionSummaryData[entity]?.[network];
          const networkMetricsTimeframe = resultsMetrics[entity]?.[network]?.[timeframeForEval];

          if (!networkSubmissionSummary) {
            if (overallStatus === 'green') overallStatus = 'amber';
            console.warn(`FTSO Submissions Evaluator: Submission summary data missing for ${entity}/${network}`);
            continue; 
          }

          if (networkMetricsTimeframe) {
            const lateCount = networkMetricsTimeframe.late || 0;
            const missingCount = networkMetricsTimeframe.missing || 0;
            const invalidCount = networkMetricsTimeframe.invalid || 0;
            const totalLMI = lateCount + missingCount + invalidCount;

            if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.RED_MIN) {
              overallStatus = 'red';
            } else if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.AMBER_MIN && overallStatus !== 'red') {
              overallStatus = 'amber';
            }
          } else {
            if (overallStatus === 'green') overallStatus = 'amber';
            console.warn(`FTSO Submissions Evaluator: ${timeframeForEval} metrics data missing in resultsMetrics for ${entity}/${network}.`);
          }
          
          if (overallStatus === 'red') break; 

          for (const type of submissionTypes) {
            const summaryDataForTimeframe = networkSubmissionSummary?.[type]?.[timeframeForEval];
  
            if (!summaryDataForTimeframe) {
              if (overallStatus === 'green') overallStatus = 'amber'; 
              console.warn(`FTSO Submissions Evaluator: ${timeframeForEval} summary data missing for ${entity}/${network}/${type}`);
              continue; 
            }
  
            const percentage = summaryDataForTimeframe.percentage;
            if (typeof percentage === 'number') {
              if (percentage < FTSO_SUBMISSIONS_THRESHOLDS.PERCENTAGE.RED_BELOW) {
                overallStatus = 'red';
              } else if (percentage < FTSO_SUBMISSIONS_THRESHOLDS.PERCENTAGE.AMBER_BELOW && overallStatus !== 'red') {
                overallStatus = 'amber';
              }
            }
  
            const zeroCount = summaryDataForTimeframe.zero_count; 
            if (typeof zeroCount === 'number') {
              if (zeroCount >= FTSO_SUBMISSIONS_THRESHOLDS.ZERO_SUBS_COUNT.RED_MIN) {
                overallStatus = 'red';
              } else if (zeroCount >= FTSO_SUBMISSIONS_THRESHOLDS.ZERO_SUBS_COUNT.AMBER_MIN && overallStatus !== 'red') {
                overallStatus = 'amber';
              }
            }
            
            if (overallStatus === 'red') break; 
          }
          if (overallStatus === 'red') break; 
        }
        if (overallStatus === 'red') break; 
      }
      return overallStatus;
    },

    registration: (data) => {
      if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        return 'red'; // No data
      }

      const entities = ['UYS', 'ENV'];
      const networks = ['flare', 'songbird'];
      let overallStatus = 'green'; // Assume green unless a problem is found

      for (const entity of entities) {
        for (const network of networks) {
          const registrationInfo = data[entity]?.[network];
          if (!registrationInfo) {
            // Data missing for this entity/network combination
            // This case might need specific handling, e.g., if missing data should be red or amber
            // For now, if any part is missing, let's consider it problematic for a 'green' status.
            // If UYS/flare is expected but missing, it's an issue.
            // However, if the structure guarantees presence, this check might be overly cautious.
            // Based on app.py, it seems these keys should always exist.
            // If they don't, it's a data integrity issue, so 'red' is appropriate.
        return 'red';
          }

          // Check current epoch registration
          if (registrationInfo.registered_current_epoch === false) {
            overallStatus = 'red';
            break; // Found a non-registered status, no need to check further
          }

          // Check next epoch pre-registration
          if (registrationInfo.preregistered_next_epoch === false) {
            overallStatus = 'red';
            break; // Found a non-preregistered status
          }
        }
        if (overallStatus === 'red') {
          break; // Exit outer loop if already red
        }
      }
      return overallStatus;
    },

    fastUpdates: (data) => {
        if (!data) return 'red';

        let hasRed = false;
        let hasAmber = false;

        Object.entries(data).forEach(([entity, networks]) => {
          Object.entries(networks).forEach(([network, items]) => {
            // Use server-computed age_secs; worst feed determines status
            const maxAge = Math.max(...items.map(item => {
              if (item.age_secs != null) return item.age_secs;
              const s = String(item.time_since_last_update || '');
              const match = s.match(/([\d.]+)s/);
              return match ? parseFloat(match[1]) : 0;
            }));

            if (maxAge >= 30) hasRed = true;
            else if (maxAge >= 15) hasAmber = true;
          });
        });

        if (hasRed) return 'red';
        if (hasAmber) return 'amber';
        return 'green';
    },

    // NEW: Evaluate nodes using node_heads.json (accurate pre-calculated alignment)
    nodesWithHeads: (nodeHeadsData, nodeInfoData = null) => {
      if (!nodeHeadsData || typeof nodeHeadsData !== 'object' || Object.keys(nodeHeadsData).length === 0) {
        console.warn("Nodes Evaluator (node_heads): nodeHeadsData is null, not an object, or empty.");
        return 'red';
      }

      let overallStatus = 'green';
      let healthyCount = 0;
      let totalNodes = 0;
      let hasAmber = false;
      let hasRed = false;
      
      const entities = Object.keys(nodeHeadsData).filter(key => key === 'UYS' || key === 'ENV');
      if (entities.length === 0) {
        console.warn("Nodes Evaluator (node_heads): No UYS or ENV entities found in nodeHeadsData.");
        return 'red';
      }

      for (const entity of entities) {
        const entityData = nodeHeadsData[entity];
        if (!entityData || typeof entityData !== 'object' || Object.keys(entityData).length === 0) {
          console.warn(`Nodes Evaluator (node_heads): Entity data for ${entity} is missing or empty.`);
          continue;
        }

        for (const nodeKey of Object.keys(entityData)) {
          const nodeHeadData = entityData[nodeKey];
          if (!nodeHeadData || typeof nodeHeadData !== 'object') {
            console.warn(`Nodes Evaluator (node_heads): Node data for ${entity} > ${nodeKey} is missing or invalid.`);
            continue;
          }

          totalNodes++;
          
          // Use pre-calculated blocks_behind from node_heads.json
          const blocksBehind = nodeHeadData.blocks_behind ?? 0;
          const nodeHeight = nodeHeadData.node_height ?? 0;
          const externalHeight = nodeHeadData.external_height ?? 0;

          // Determine status based on blocks_behind using thresholds
          let status = 'synced';
          let thresholdKey = nodeKey.toUpperCase();
          
          // Map node keys to threshold keys
          if (thresholdKey === 'BITCOIN') thresholdKey = 'BTC';
          else if (thresholdKey === 'DOGECOIN') thresholdKey = 'DOGE';
          else if (thresholdKey === 'ETHEREUM') thresholdKey = 'ETH';
          else if (thresholdKey.startsWith('FLARE')) thresholdKey = 'FLR';
          else if (thresholdKey.startsWith('SONGBIRD')) thresholdKey = 'SGB';
          
          const thresholds = EXTERNAL_VALIDATION_THRESHOLDS[thresholdKey] || EXTERNAL_VALIDATION_THRESHOLDS.DEFAULT;
          
          if (blocksBehind <= 0 || blocksBehind <= thresholds.GREEN_MAX) {
            status = 'synced';
            healthyCount++;
          } else if (blocksBehind <= thresholds.AMBER_MAX) {
            status = 'behind';
            hasAmber = true;
          } else {
            status = 'significantly_behind';
            hasRed = true;
            console.warn(`Nodes Evaluator (node_heads): Node ${entity} > ${nodeKey} is significantly behind external chain (${blocksBehind} blocks). Node: ${nodeHeight}, External: ${externalHeight}`);
          }

          // Check simple_health from node_info.json if available (for additional health indicators)
          if (nodeInfoData && nodeInfoData[entity] && nodeInfoData[entity][nodeKey]) {
            const nodeInfo = nodeInfoData[entity][nodeKey];
            const simpleHealth = nodeInfo.simple_health;
            
            if (['Unhealthy', 'NETWORK ERROR', 'ERROR', 'TASK FAILED', 'Behind'].includes(simpleHealth)) {
              console.warn(`Nodes Evaluator (node_heads): Node ${entity} > ${nodeKey} has critical simple_health: ${simpleHealth}`);
              hasRed = true;
            } else if (simpleHealth === 'Slightly Behind') {
              hasAmber = true;
            }
          }
        }
      }

      // Final status determination
      if (hasRed) {
        console.warn("Nodes Evaluator (node_heads): Overall status is RED due to significantly behind nodes or critical health issues.");
        return 'red';
      }

      if (totalNodes === 0) {
        console.warn("Nodes Evaluator (node_heads): No nodes processed, defaulting to red.");
        return 'red';
      }

      const healthyPercentage = totalNodes > 0 ? (healthyCount / totalNodes) * 100 : 0;
      
      if (healthyPercentage < NODE_THRESHOLDS.HEALTHY_PERCENTAGE.RED_BELOW) {
        overallStatus = 'red';
      } else if (healthyPercentage < NODE_THRESHOLDS.HEALTHY_PERCENTAGE.AMBER_BELOW || hasAmber) {
        overallStatus = 'amber';
      }
      
      return overallStatus;
    },

    // Original nodes function (kept for fallback)
    nodes: (nodeData, externalChainData = null) => {
      // console.log("Evaluating node data:", JSON.stringify(nodeData, null, 2));
      if (!nodeData || typeof nodeData !== 'object' || Object.keys(nodeData).length === 0 || !nodeData.UYS || !nodeData.ENV) {
        console.warn("Nodes Evaluator: Initial nodeData is null, not an object, empty, or missing UYS/ENV entities.");
        return 'red';
      }
  
      let overallStatus = 'green';
      let healthyCount = 0;
      let totalNodes = 0;
      let hasAmber = false;
      let hasRed = false;
      let isEmptyStructure = false; // Flag for empty inner structures
  
      const entities = Object.keys(nodeData).filter(key => key === 'UYS' || key === 'ENV');
      if (entities.length === 0) {
        console.warn("Nodes Evaluator: No UYS or ENV entities found in nodeData.");
        isEmptyStructure = true; // No relevant entities
      }

      for (const entity of entities) {
        const entityData = nodeData[entity];
        if (!entityData || typeof entityData !== 'object' || Object.keys(entityData).length === 0) {
          console.warn(`Nodes Evaluator: Entity data for ${entity} is missing or empty.`);
          isEmptyStructure = true;
          continue; // Skip to next entity if this one is empty
        }
  
        for (const networkKey of Object.keys(entityData)) {
          const networkData = entityData[networkKey];
          if (!networkData || typeof networkData !== 'object' || Object.keys(networkData).length === 0) {
            console.warn(`Nodes Evaluator: Network data for ${entity} > ${networkKey} is missing or empty.`);
            isEmptyStructure = true;
            continue; // Skip to next network if this one is empty
          }

          // Each networkKey directly represents a node here, e.g., "BITCOIN", "FLARE_OBS"
          totalNodes++;
          const node = networkData; // networkData is the node object itself

          // Check external validation first (if available)
          let externalValidation = node.external_validation;
          
          // If no embedded validation, optionally compute from externalChainData
          if (!externalValidation && externalChainData && typeof externalChainData === 'object') {
            const networkUpper = (networkKey || '').toString().toUpperCase();
            const chainMapping = {
              'BITCOIN': 'BTC',
              'DOGECOIN': 'DOGE',
              'ETHEREUM': 'ETH',
              'FLARE': 'FLR',
              'FLARE_OBS': 'FLR',
              'FLARE_OBS2': 'FLR',
              'FLARE_VAL': 'FLR',
              'FLARE_VAL2': 'FLR',
              'SONGBIRD': 'SGB',
              'SONGBIRD_OBS': 'SGB',
              'SONGBIRD_OBS2': 'SGB',
              'XRPL': 'XRPL'
            };
            const externalChain = chainMapping[networkUpper];
            const info = externalChain ? externalChainData[externalChain] : null;
            if (info && info.status === 'success' && typeof info.height === 'number') {
              // Determine node height from chain data
              const nodeHeightRaw = node.chain?.latest_block ?? node.chain?.blocks ?? node.chain?.headers ?? node.latest_block ?? node.blocks ?? node.headers;
              const nodeHeight = parseInt(nodeHeightRaw, 10);
              if (!Number.isNaN(nodeHeight)) {
                const blocksBehind = info.height - nodeHeight;
                const thresholds = EXTERNAL_VALIDATION_THRESHOLDS[externalChain] || EXTERNAL_VALIDATION_THRESHOLDS.DEFAULT;
                let status;
                if (blocksBehind <= 0) status = 'synced';
                else if (blocksBehind <= thresholds.GREEN_MAX) status = 'synced';
                else if (blocksBehind <= thresholds.AMBER_MAX) status = 'behind';
                else status = 'significantly_behind';
                externalValidation = { status, blocks_behind: blocksBehind, external_height: info.height, node_height: nodeHeight };
              }
            }
          }
          if (externalValidation) {
            const validationStatus = externalValidation.status;
            if (validationStatus === 'significantly_behind') {
              console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} is significantly behind external chain (${externalValidation.blocks_behind} blocks)`);
              hasRed = true;
            } else if (validationStatus === 'behind') {
              console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} is behind external chain (${externalValidation.blocks_behind} blocks)`);
              hasAmber = true;
            } else if (validationStatus === 'slightly_behind') {
              hasAmber = true;
            } else if (['no_external_data', 'stale_external_data', 'no_node_height'].includes(validationStatus)) {
              // Don't penalize for missing external data, fall back to simple_health
            }
          }

          // Explicitly check simple_health for critical errors
          const simpleHealth = node.simple_health;
          if (['Unhealthy', 'NETWORK ERROR', 'ERROR', 'TASK FAILED', 'Behind'].includes(simpleHealth)) {
            console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} has critical simple_health: ${simpleHealth}`);
            hasRed = true;
          } else if (simpleHealth === 'Healthy') {
            healthyCount++;
          } else if (simpleHealth === 'Slightly Behind') {
            hasAmber = true;
            healthyCount++; // Still count as functional
          } else {
            // Any other simple_health that isn't explicitly 'Healthy' or a critical error might be amber
            // For now, let's consider 'Unknown' or other non-critical, non-healthy states as amber.
            // If simple_health is 'Unknown' or some other non-critical state, it implies not fully healthy.
            hasAmber = true;
          }

          // Check if the 'chain' or 'database' fields show an error status or are null (if expected)
          // This part depends on how you want to treat 'chain' and 'database' errors.
          // If node.status (top-level status for the node from Python) is 'error' or 'TASK FAILED'
          if (
            node.status === 'error' ||
            node.status === 'TASK FAILED' ||
            node.status === 'NETWORK ERROR' ||
            (typeof node.status === 'string' && node.status.toLowerCase() === 'syncing')
          ) {
             console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} has status: ${node.status}`);
             hasRed = true;
          }

          if (node.chain && node.chain.error) {
            console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} chain has error: ${node.chain.error}`);
            hasRed = true;
          }
          if (node.chain && typeof node.chain.status === 'string' && node.chain.status.toLowerCase() === 'syncing') {
            hasRed = true;
          }
          // If a database section exists and has an error
          if (node.database && (node.database.status === 'error' || (typeof node.database.status === 'string' && node.database.status.toLowerCase() === 'syncing'))) {
            console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} database has error: ${node.database.error}`);
            hasRed = true; 
          }
          // Treat remaining/progress indicators as red when actively syncing/indexing
          const toNum = (v) => {
            if (v === null || v === undefined) return NaN;
            if (typeof v === 'number') return v;
            if (typeof v === 'string') {
              const cleaned = v.replace(/%/g, '').trim();
              const n = parseFloat(cleaned);
              return isNaN(n) ? NaN : n;
            }
            return NaN;
          };
          
          // Only check chain sync progress, not database indexing progress
          // Database indexing can continue in background without affecting node health
          const chainRemaining = toNum(node.chain?.remaining);
          const chainProgress = toNum(node.chain?.progress);
          const chainStatus = (node.chain?.status || '').toString().toLowerCase();
          const nodeStatusLower = (node.status || '').toString().toLowerCase();
          
          // Treat as red only if the chain is actively syncing per status; else at most amber
          if (!isNaN(chainRemaining) && chainRemaining > 0) {
            if (chainStatus === 'syncing' || nodeStatusLower === 'syncing') {
              console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} has chain remaining (syncing): ${chainRemaining}`);
              hasRed = true;
            } else {
              hasAmber = true;
            }
          }
          if (!isNaN(chainProgress) && chainProgress < 100) {
            if (chainStatus === 'syncing' || nodeStatusLower === 'syncing') {
              console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} has chain progress (syncing): ${chainProgress}%`);
              hasRed = true;
            } else {
              hasAmber = true;
            }
          }
          
          // For database-only issues, only mark red if database status is explicitly 'error'
          if (node.database?.status === 'error') {
            console.warn(`Nodes Evaluator: Node ${entity} > ${networkKey} has database error: ${node.database.error}`);
            hasRed = true;
          }

          // Database lag evaluation: compare database.latest_block vs chain.latest_block using thresholds
          const dbLatestRaw = node?.database?.latest_block;
          const chainLatestRaw = node?.chain?.latest_block;
          const dbLatest = dbLatestRaw != null ? parseInt(dbLatestRaw, 10) : NaN;
          const chainLatest = chainLatestRaw != null ? parseInt(chainLatestRaw, 10) : NaN;
          if (!Number.isNaN(dbLatest) && !Number.isNaN(chainLatest)) {
            const dbBlocksBehind = chainLatest - dbLatest;
            let thresholdKey = networkKey.toUpperCase();
            if (thresholdKey === 'BITCOIN') thresholdKey = 'BTC';
            else if (thresholdKey === 'DOGECOIN') thresholdKey = 'DOGE';
            // XRPL stays XRPL; others fallback to DEFAULT
            const dbThresholds = DATABASE_SYNC_THRESHOLDS[thresholdKey] || DATABASE_SYNC_THRESHOLDS.DEFAULT;
            if (dbBlocksBehind > dbThresholds.GREEN_MAX) {
              if (dbBlocksBehind >= (dbThresholds.RED_MIN ?? Number.MAX_SAFE_INTEGER) || dbBlocksBehind > (dbThresholds.AMBER_MAX ?? dbThresholds.GREEN_MAX)) {
                hasRed = true;
              } else {
                hasAmber = true;
              }
            }
          }

          // Additional DB severity: explicit blocks_remaining if present
          const blocksRemainingRaw = node?.database?.blocks_remaining;
          const blocksRemaining = blocksRemainingRaw != null ? parseInt(blocksRemainingRaw, 10) : NaN;
          if (!Number.isNaN(blocksRemaining)) {
            let thresholdKey2 = networkKey.toUpperCase();
            if (thresholdKey2 === 'BITCOIN') thresholdKey2 = 'BTC';
            else if (thresholdKey2 === 'DOGECOIN') thresholdKey2 = 'DOGE';
            const dbThresholds2 = DATABASE_SYNC_THRESHOLDS[thresholdKey2] || DATABASE_SYNC_THRESHOLDS.DEFAULT;
            if (blocksRemaining > dbThresholds2.GREEN_MAX) {
              if (blocksRemaining >= (dbThresholds2.RED_MIN ?? Number.MAX_SAFE_INTEGER) || blocksRemaining > (dbThresholds2.AMBER_MAX ?? dbThresholds2.GREEN_MAX)) {
                hasRed = true;
              } else {
                hasAmber = true;
              }
            }
          }
        }
      }
      
      // If any node triggered a critical red condition, the overall status is red.
      if (hasRed) {
        console.warn("Nodes Evaluator: Overall status is RED due to critical simple_health or error in at least one node/chain/database.");
        return 'red';
      }

      if (isEmptyStructure) {
        console.warn("Nodes Evaluator: Red due to empty or missing critical structures (entity, network, or node type).");
        return 'red';
      }
  
      if (totalNodes === 0) { // Should be caught by isEmptyStructure earlier, but as a fallback
        console.warn("Nodes Evaluator: No nodes processed, defaulting to red.");
        return 'red';
      }
      
      // Existing threshold logic (now acts as a fallback if no hard 'red' conditions met)
      const healthyPercentage = totalNodes > 0 ? (healthyCount / totalNodes) * 100 : 0;
      // console.log(`Nodes Evaluator: Healthy count: ${healthyCount}, Total nodes: ${totalNodes}, Healthy percentage: ${healthyPercentage.toFixed(2)}%`);

      if (healthyPercentage < NODE_THRESHOLDS.HEALTHY_PERCENTAGE.RED_BELOW) {
        overallStatus = 'red';
      } else if (healthyPercentage < NODE_THRESHOLDS.HEALTHY_PERCENTAGE.AMBER_BELOW || hasAmber) { 
        // If any node was amber (e.g. unknown simple_health) and we are not red, make overall amber
          overallStatus = 'amber'; 
      }
      
      // console.log(`Nodes Evaluator: Final overallStatus: ${overallStatus}`);
      return overallStatus;
    },

    validators: (data) => {
      if (!data || typeof data !== 'object') return 'red';
      
      // Get all validator keys dynamically
      const validatorKeys = Object.keys(data);
      if (validatorKeys.length === 0) {
        return 'red'; // No validators found
      }

      // Check if all validators are connected
      let allConnected = true;
      let hasAtLeastOneValidator = false;

      for (const key of validatorKeys) {
        if (data[key] && typeof data[key] === 'object') {
          hasAtLeastOneValidator = true;
          if (data[key].connected === false) {
            return 'red'; // Any validator disconnected = red
          }
          if (data[key].connected !== true) {
            allConnected = false; // Undefined/null connection status
          }
        }
      }

      if (!hasAtLeastOneValidator) {
        return 'red'; // No valid validator objects found
      }

      if (allConnected) {
        return 'green';
      }
      
      // Default to red if statuses are not explicitly true/false (e.g., undefined, null)
      return 'red'; 
  },

    // New function to evaluate status for a single FTSO submission summary box
    evaluateFtsoSummaryBoxStatus: (submissionSummaryDataForBox, resultsMetricsForBox, submit2PricesDataForBox, feedConfigDataForBox) => {
      if (!submissionSummaryDataForBox || typeof submissionSummaryDataForBox !== 'object' || Object.keys(submissionSummaryDataForBox).length === 0 ||
          !resultsMetricsForBox || typeof resultsMetricsForBox !== 'object' ||
          !submit2PricesDataForBox || typeof submit2PricesDataForBox !== 'object' ||
          !feedConfigDataForBox || typeof feedConfigDataForBox !== 'object' ) {
        // Missing inputs should not label the box red; caller may use last-good status.
        return null; 
      }
  
      let overallStatus = 'green'; 
      const submissionTypes = ['submit1', 'submit2', 'submitSignatures'];
      const timeframeForEval = FTSO_EVALUATION_TIMEFRAME; // Use imported constant

      // Part 1: Hit Rate Check (from original ftsoSubmissions)
      let expectedFeeds = (feedConfigDataForBox || [])
        .filter(feed => feed?.feed_name?.trim().toLowerCase() !== 'null' && feed.submit === true)
        .map(feed => feed.feed_name.trim().toLowerCase());
      
      const feedCount = expectedFeeds.length;

      if (feedCount > 0) { // Only evaluate hit rate if there are expected feeds
        const latestVotingRound = submissionSummaryDataForBox.latest_voting_round;
        const lastVotingRoundForTimeframe = submissionSummaryDataForBox[`${timeframeForEval}_last_voting_round`];

        let totalResultSumTimeframe = 0;
        let totalExpectedSubmissionsTimeframe = 0;

        // Ensure lastVotingRoundForTimeframe and latestVotingRound are numbers before looping
        if (typeof lastVotingRoundForTimeframe === 'number' && typeof latestVotingRound === 'number') {
            for (let round = lastVotingRoundForTimeframe + 1; round <= latestVotingRound; round++) {
                const roundStr = round.toString();
                const roundResults = submit2PricesDataForBox[roundStr] || [];
                const feedResultsMap = {};
                roundResults.forEach(feedData => {
                    const feedKey = feedData.feed?.trim().toLowerCase();
                    if(feedKey) feedResultsMap[feedKey] = Number(feedData.result);
                });

                expectedFeeds.forEach(feedName => {
                    totalExpectedSubmissionsTimeframe += 1;
                    const result = feedResultsMap[feedName];
                    if (typeof result === 'number' && !isNaN(result)) {
                    totalResultSumTimeframe += result;
                    } else {
                    totalResultSumTimeframe += 0; 
                    }
                });
            }
        }
        
        let hitRateTimeframe = 0;
        if (totalExpectedSubmissionsTimeframe > 0) {
          hitRateTimeframe = (totalResultSumTimeframe / totalExpectedSubmissionsTimeframe) * 100;
        } else if (feedCount > 0) {
          hitRateTimeframe = 0;
        }

        if (hitRateTimeframe < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.RED_BELOW) {
          overallStatus = 'red';
        } else if (hitRateTimeframe < FTSO_SUMMARY_HIT_RATE_THRESHOLDS.AMBER_BELOW && overallStatus !== 'red') {
          overallStatus = 'amber';
        }
      } else {
        // No expected feeds configured for this entity/network.
        // This might be considered 'green' or 'grey' or a specific status.
        // For now, let's not penalize it if there are no feeds to check.
        // If overallStatus is still 'green', it remains 'green'.
      }

      // Part 2: LMI Count Check (from original ftsoSubmissions)
      if (overallStatus !== 'red') { // Only proceed if not already red
        if (resultsMetricsForBox && resultsMetricsForBox[timeframeForEval]) {
          const metricsTimeframe = resultsMetricsForBox[timeframeForEval];
          const lateCount = metricsTimeframe.late || 0;
          const missingCount = metricsTimeframe.missing || 0;
          const invalidCount = metricsTimeframe.invalid || 0;
          const totalLMI = lateCount + missingCount + invalidCount;

          if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.RED_MIN) {
            overallStatus = 'red';
          } else if (totalLMI >= FTSO_SUBMISSIONS_THRESHOLDS.LATE_MISSING_INVALID_COUNT.AMBER_MIN && overallStatus !== 'red') {
            overallStatus = 'amber';
          }
        } else {
          if (overallStatus === 'green') overallStatus = 'amber'; // Data missing for LMI, consider amber
          // console.warn(`FTSO Summary Box Evaluator: ${timeframeForEval} metrics data missing in resultsMetricsForBox.`);
        }
      }
      
      // Part 3: Submission Percentages and Zero Counts (from original ftsoSubmissions)
      if (overallStatus !== 'red') { // Only proceed if not already red
        for (const type of submissionTypes) {
          const summaryDataForTimeframe = submissionSummaryDataForBox?.[type]?.[timeframeForEval];

          if (!summaryDataForTimeframe) {
            if (overallStatus === 'green') overallStatus = 'amber'; 
            // console.warn(`FTSO Summary Box Evaluator: ${timeframeForEval} summary data missing for ${type}`);
            continue; 
          }

          const percentage = summaryDataForTimeframe.percentage;
          if (typeof percentage === 'number') {
            if (percentage < FTSO_SUBMISSIONS_THRESHOLDS.PERCENTAGE.RED_BELOW) {
              overallStatus = 'red';
            } else if (percentage < FTSO_SUBMISSIONS_THRESHOLDS.PERCENTAGE.AMBER_BELOW && overallStatus !== 'red') {
              overallStatus = 'amber';
            }
          }

          const zeroCount = summaryDataForTimeframe.zero_count; 
          if (typeof zeroCount === 'number') {
            if (zeroCount >= FTSO_SUBMISSIONS_THRESHOLDS.ZERO_SUBS_COUNT.RED_MIN) {
              overallStatus = 'red';
            } else if (zeroCount >= FTSO_SUBMISSIONS_THRESHOLDS.ZERO_SUBS_COUNT.AMBER_MIN && overallStatus !== 'red') {
              overallStatus = 'amber';
            }
          }
          if (overallStatus === 'red') break; 
        }
      }
      return overallStatus;
    },

    // New function to evaluate status for a single FDC submission summary box
    evaluateFdcSummaryBoxStatus: (submissionSummaryDataForBox) => {
      if (!submissionSummaryDataForBox || typeof submissionSummaryDataForBox !== 'object' || Object.keys(submissionSummaryDataForBox).length === 0) {
        return null; 
      }

      // Get 15min attestation data
      const fifteenMinData = submissionSummaryDataForBox?.['15min'];
      if (!fifteenMinData) {
        return null;
      }

      // Factor 1: Attestation percentage logic
      const valid = fifteenMinData?.attestations?.valid || fifteenMinData?.valid_attestations || 0;
      const total = fifteenMinData?.attestations?.total || fifteenMinData?.total_attestations || 0;

      let attestationStatus = 'red';
      if (total === 0) {
        attestationStatus = 'red';
      } else {
        const percentage = (valid / total) * 100;
        if (percentage === 100) {
          attestationStatus = 'green';
        } else if (percentage >= 85) {
          attestationStatus = 'amber';
        } else {
          attestationStatus = 'red';
        }
      }

      // Factor 2: Reveal offences logic
      const offencesCount = fifteenMinData?.reveal_offences?.count || 0;
      let offencesStatus = 'red';
      
      if (offencesCount === 0) {
        offencesStatus = 'green';
      } else if (offencesCount >= 1 && offencesCount <= 2) {
        offencesStatus = 'amber';
      } else {
        offencesStatus = 'red'; // 3+ offences
      }

      // Return the worst status (most restrictive)
      if (attestationStatus === 'red' || offencesStatus === 'red') {
        return 'red';
      } else if (attestationStatus === 'amber' || offencesStatus === 'amber') {
        return 'amber';
      } else {
        return 'green'; // Both are green
      }
    },

    fdcSubmissions: (submissionSummaryData, fdcData) => {
      if (!submissionSummaryData || typeof submissionSummaryData !== 'object' || Object.keys(submissionSummaryData).length === 0) {
        console.warn("FDC Submissions Evaluator: Missing submission summary data.");
        return 'red'; 
      }

      const entities = ['UYS', 'ENV'];
      const networks = ['flare', 'songbird'];
      const boxStatuses = [];

      // Helper function to get default FDC data (same as individual headers)
      const getDefaultFDCData = () => ({
        '15min': {
          attestations: { percentage: 0 },
          signatures: { percentage: 0 },
          reveal_offences: { count: 0 },
          missing_rounds: []
        },
        '2hr': {
          attestations: { percentage: 0 },
          signatures: { percentage: 0 },
          reveal_offences: { count: 0 },
          missing_rounds: []
        },
        '6hr': {
          attestations: { percentage: 0 },
          signatures: { percentage: 0 },
          reveal_offences: { count: 0 },
          missing_rounds: []
        }
      });

      // Evaluate all individual boxes first
      for (const entity of entities) {
        for (const network of networks) {
          // Use same fallback logic as individual headers
          const entitySubmissionSummary = submissionSummaryData[entity]?.[network] || getDefaultFDCData();

          // Use the same evaluation logic as individual FDC summary boxes
          const boxStatus = StatusEvaluator.evaluateFdcSummaryBoxStatus(entitySubmissionSummary);
          boxStatuses.push(boxStatus);
        }
      }

      // Aggregate all box statuses: if ANY red -> red, if ANY amber -> amber, else green
      if (boxStatuses.includes('red')) {
        return 'red';
      } else if (boxStatuses.includes('amber')) {
        return 'amber';
      } else {
        return 'green';
      }
    },
};